import React, { useMemo } from 'react';
import Joyride, { CallBackProps, Step, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

interface OnboardingTourProps {
  steps: Step[];
  run: boolean;
  onComplete: () => void;
  onSkip: () => void;
  continuous?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({
  steps,
  run,
  onComplete,
  onSkip,
  continuous = true,
  showProgress = true,
  showSkipButton = true
}) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action, type } = data;

    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      if (status === STATUS.FINISHED) {
        onComplete();
      } else if (status === STATUS.SKIPPED) {
        onSkip();
      }
    }

    // Handle skip button click
    if (type === EVENTS.STEP_AFTER && action === ACTIONS.CLOSE) {
      onSkip();
    }
  };

  const locale = {
    back: t('onboarding.back'),
    close: t('onboarding.close'),
    last: t('onboarding.finish'),
    next: t('onboarding.next'),
    open: t('onboarding.next'),
    skip: t('onboarding.skip')
  };

  const tourStyles = useMemo(() => ({
    options: {
      arrowColor: isDark ? '#1f2937' : '#fff',
      backgroundColor: isDark ? '#1f2937' : '#fff',
      overlayColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
      primaryColor: '#3b82f6',
      textColor: isDark ? '#f3f4f6' : '#1f2937',
      width: 400,
      zIndex: 10000,
    },
    tooltip: {
      borderRadius: '16px',
      padding: '24px',
    },
    tooltipContainer: {
      textAlign: 'left' as const,
    },
    tooltipTitle: {
      fontSize: '20px',
      fontWeight: '700' as const,
      marginBottom: '12px',
      color: isDark ? '#f3f4f6' : '#111827',
    },
    tooltipContent: {
      fontSize: '15px',
      lineHeight: '1.6',
      color: isDark ? '#d1d5db' : '#4b5563',
      padding: '8px 0',
    },
    buttonNext: {
      background: '#0f7f75',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600' as const,
      padding: '10px 20px',
      transition: 'all 0.2s',
    },
    buttonBack: {
      color: isDark ? '#9ca3af' : '#6b7280',
      fontSize: '14px',
      fontWeight: '600' as const,
      marginRight: '12px',
    },
    buttonSkip: {
      color: '#9ca3af',
      fontSize: '13px',
      fontWeight: '500' as const,
    },
    beacon: {
      background: '#0f7f75',
    },
    beaconInner: {
      background: '#0f7f75',
    },
    beaconOuter: {
      background: '#0f7f75',
      border: '2px solid #3b82f6',
    }
  }), [isDark]);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={continuous}
      showProgress={showProgress}
      showSkipButton={showSkipButton}
      callback={handleJoyrideCallback}
      locale={locale}
      styles={tourStyles}
      floaterProps={{
        disableAnimation: false,
        styles: {
          arrow: {
            length: 8,
            spread: 16
          }
        }
      }}
    />
  );
};

export default OnboardingTour;
